import {useMemo} from 'react';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import {useHistory, useLocation} from 'react-router-dom';
import Table from './Table';
import Filters from './Filters';
import {useUser} from '@postinumero/authorization';
import {isMoment} from "moment/moment";
import {useInvoices} from "../../../../api";
import {
  DONE,
  DRAFT,
  INVALID,
  INVOICESENT,
  MARKEDPAID,
  PAID,
  READYTOSEND,
  SENDINGFAILED,
  SENDINGREQUESTED
} from "../../../../shared/Statuses";
import MomentAdapter from "@date-io/moment";
import {CONSOLIDATED, OWN, RECEIVED} from "../../../../shared/Types";


const formatDate = date => {
  if (isMoment(date)) {
    return date.format('YYYY-MM-DD');
  }
  if (typeof date === 'string' && date.length > 0) {
    return date;
  }
  return null;
}

const useParams = ({eligibleStatuses, eligibleTypes}) => {

  const location = useLocation();
  const history = useHistory();

  const filters = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const search = sp.get('search') ?? '';
    const endDate = sp.get('endDate');
    let startDate = sp.get('startDate');
    if (typeof startDate !== "string" || startDate.length === 0) {
      startDate = formatDate(new MomentAdapter().date().add(-1, 'months'));
    }
    const statuses = sp.getAll('statuses').filter(v => eligibleStatuses.includes(v));
    const types = sp.getAll('types').filter(v => eligibleTypes.includes(v));

    return {
      search, endDate, startDate, statuses, types
    };
  }, [location.search, eligibleStatuses, eligibleTypes]);

  const params = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const ca = parseInt(params.get('ca'));
    return {ca, filters};
  }, [location.search, filters]);

  const setParams = ({ca, filters = {}}) => {
    const sp = new URLSearchParams();
    if (Number.isSafeInteger(ca)) {
      sp.set('ca', ca);
    }

    ['search', 'endDate', 'startDate'].forEach(name => {
      const value = filters[name];
      if (typeof value === 'string' && value.length > 0) {
        sp.set(name, value);
      }
    });

    ['statuses', 'types'].forEach(name => {
      const value = filters[name];
      if (Array.isArray(value) && value.length > 0) {
        value.forEach(v => sp.append(name, v));
      }
    });

    const qs = sp.toString();
    if (qs !== location.search) {
      history.push({pathname: '/invoices', search: qs});
    }
  };

  return [params, setParams];
};


export default function Layout() {
  const user = useUser();
  const isIA = user.roles.includes('Issuer agent');
  const isI = user.roles.includes('Inspector');
  const eligibleStatuses = useMemo(() => isIA
      ? [DRAFT, READYTOSEND, SENDINGREQUESTED, SENDINGFAILED, INVOICESENT, MARKEDPAID, PAID, INVALID, DONE]
      : [DRAFT, READYTOSEND, SENDINGREQUESTED, SENDINGFAILED, INVOICESENT, MARKEDPAID, PAID, INVALID],
    [isIA]);

  const eligibleTypes = useMemo(() => isIA
      ? [RECEIVED, CONSOLIDATED]
      : [OWN, RECEIVED, CONSOLIDATED],
    [isIA]);

  const [params, setParams] = useParams({eligibleStatuses, eligibleTypes});
  if ((isI || isIA) && params.filters.types.length === 0){
    params.filters.types.push(RECEIVED);
    if (isI) {
      params.filters.types.push(OWN);
    }
  }

  const {isLoading, data: invoices} = useInvoices(params.filters);

  const resetFilters = () => {
    setParams({});
  };

  const setFilters = (filters) => {
    const {startDate, endDate} = filters;
    filters = {...filters, startDate: formatDate(startDate), endDate: formatDate(endDate)};
    setParams({...params, filters});
  }

  // FILTER BY TYPE: Own_consolidated_invoices, Own_commission_fee_invoices, Received_commission_fee_invoices

  return (
    <Grid container spacing={5}>
      <Grid item xs={12}>
        <Filters activeFilters={params.filters} setFilters={setFilters} statuses={eligibleStatuses} types={eligibleTypes} reset={resetFilters}/>
      </Grid>
      <Grid item xs={12}>
        {isLoading && (<span><CircularProgress size='1rem'/> Loading ...</span>)}
        {!isLoading && (<Table invoices={invoices} user={user}/>)}
      </Grid>
    </Grid>
  );
}
